import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ScrollArea } from '../../ui/scroll-area';
import { formatTimeOfDay, pad2 } from '../time-of-day-select';
import type { TimeOfDay } from '../time-of-day-select';

// ─── Constants ────────────────────────────────────────────────────────────────

const HOURS_12H = Array.from({ length: 12 }, (_, index) => index + 1);
const HOURS_24H = Array.from({ length: 24 }, (_, index) => index);
const MINUTE_STEP = 5;
const MINUTES = [...Array.from({ length: 60 / MINUTE_STEP }, (_, index) => index * MINUTE_STEP), 59];
const AM_PM_OPTIONS = ['AM', 'PM'] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export const HOUR_CYCLE = { H12: '12h', H24: '24h' } as const;
export type HourCycle = (typeof HOUR_CYCLE)[keyof typeof HOUR_CYCLE];

/** Language-neutral internal period value — only the rendered label and aria strings are localized. */
type Period = (typeof AM_PM_OPTIONS)[number];

export interface TimeScrollPickerProps {
  /** Accessible name for the trigger (e.g. "From"), combined with the current time. */
  ariaLabel?: string;
  className?: string;
  /** Hour display/input cycle. Defaults to the cycle resolved from the active i18n language. */
  hourCycle?: HourCycle;
  onChange: (time: TimeOfDay) => void;
  value: TimeOfDay;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function to12Hour(hour24: number): number {
  const mod = hour24 % 12;
  return mod === 0 ? 12 : mod;
}

function isPM(hour24: number): boolean {
  return hour24 >= 12;
}

/** 12h + AM/PM -> 24h hour, handling the 12 AM -> 0 and 12 PM -> 12 edge cases. */
function combine12HourParts(hour12: number, pm: boolean): number {
  if (pm) return hour12 === 12 ? 12 : hour12 + 12;
  return hour12 === 12 ? 0 : hour12;
}

function periodTranslationKey(period: Period): string {
  return period === 'AM' ? 'timeScrollPicker.am' : 'timeScrollPicker.pm';
}

function formatDisplayTime(time: TimeOfDay, hourCycle: HourCycle, periodLabel: string): string {
  if (hourCycle === HOUR_CYCLE.H24) return formatTimeOfDay(time);
  return `${formatTimeOfDay({ hour: to12Hour(time.hour), minute: time.minute })} ${periodLabel}`;
}

/** Resolves the hour cycle from a BCP 47 language tag via Intl; falls back to 12h on an unknown tag. */
function getHourCycleForLanguage(language: string): HourCycle {
  try {
    const { hour12 } = new Intl.DateTimeFormat(language, { hour: 'numeric' }).resolvedOptions();
    return hour12 ? HOUR_CYCLE.H12 : HOUR_CYCLE.H24;
  } catch {
    return HOUR_CYCLE.H12;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Compact input-look trigger that opens a shadcn-style scroll-column time picker
 * (hour / minute / AM-PM columns of icon buttons). Each column click emits `onChange`
 * immediately; the popover stays open so all three parts can be set in one visit.
 */
export function TimeScrollPicker({
  ariaLabel,
  className,
  hourCycle: hourCycleProp,
  onChange,
  value,
}: TimeScrollPickerProps): React.JSX.Element {
  const { i18n, t } = useTranslation('common');
  const hourCycle = hourCycleProp ?? getHourCycleForLanguage(i18n.language);

  const selectedHourButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const selectedMinuteButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const selectedPeriodButtonRef = React.useRef<HTMLButtonElement | null>(null);

  /** Fired once the popover content mounts and receives focus — never on later clicks inside it. */
  function scrollSelectedButtonsIntoView(): void {
    selectedHourButtonRef.current?.scrollIntoView({ block: 'center' });
    selectedMinuteButtonRef.current?.scrollIntoView({ block: 'center' });
    selectedPeriodButtonRef.current?.scrollIntoView({ block: 'center' });
  }

  const currentPeriod: Period = isPM(value.hour) ? 'PM' : 'AM';
  const displayTime = formatDisplayTime(value, hourCycle, t(periodTranslationKey(currentPeriod)));
  const triggerAriaLabel = ariaLabel ? `${ariaLabel} ${displayTime}` : undefined;

  function handleHourClick(hour: number): void {
    if (hourCycle === HOUR_CYCLE.H24) {
      onChange({ ...value, hour });
      return;
    }
    onChange({ ...value, hour: combine12HourParts(hour, isPM(value.hour)) });
  }

  function handleMinuteClick(minute: number): void {
    onChange({ ...value, minute });
  }

  function handleAmPmClick(period: Period): void {
    onChange({ ...value, hour: combine12HourParts(to12Hour(value.hour), period === 'PM') });
  }

  function isHourSelected(hour: number): boolean {
    return hourCycle === HOUR_CYCLE.H24 ? value.hour === hour : to12Hour(value.hour) === hour;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label={triggerAriaLabel}
          className={cn(
            'h-8 justify-start gap-1.5 border-input bg-background px-2.5 text-sm font-normal tabular-nums shadow-none hover:bg-background',
            className,
          )}
          type="button"
          variant="outline"
        >
          {displayTime}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" onOpenAutoFocus={scrollSelectedButtonsIntoView}>
        <div className="flex h-56 divide-x">
          <ScrollArea aria-label={t('timeScrollPicker.hours')} className="w-14" role="group">
            <div className="flex flex-col p-1">
              {(hourCycle === HOUR_CYCLE.H12 ? HOURS_12H : HOURS_24H).map((hour) => (
                <Button
                  aria-pressed={isHourSelected(hour)}
                  className="w-full shrink-0"
                  key={hour}
                  onClick={(): void => handleHourClick(hour)}
                  ref={isHourSelected(hour) ? selectedHourButtonRef : undefined}
                  size="icon"
                  type="button"
                  variant={isHourSelected(hour) ? 'default' : 'ghost'}
                >
                  {hourCycle === HOUR_CYCLE.H24 ? pad2(hour) : hour}
                </Button>
              ))}
            </div>
          </ScrollArea>
          <ScrollArea aria-label={t('timeScrollPicker.minutes')} className="w-14" role="group">
            <div className="flex flex-col p-1">
              {MINUTES.map((minute) => (
                <Button
                  aria-pressed={value.minute === minute}
                  className="w-full shrink-0"
                  key={minute}
                  onClick={(): void => handleMinuteClick(minute)}
                  ref={value.minute === minute ? selectedMinuteButtonRef : undefined}
                  size="icon"
                  type="button"
                  variant={value.minute === minute ? 'default' : 'ghost'}
                >
                  {pad2(minute)}
                </Button>
              ))}
            </div>
          </ScrollArea>
          {hourCycle === HOUR_CYCLE.H12 && (
            <ScrollArea aria-label={t('timeScrollPicker.period')} className="w-14" role="group">
              <div className="flex flex-col p-1">
                {AM_PM_OPTIONS.map((period) => {
                  const selected = currentPeriod === period;
                  return (
                    <Button
                      aria-pressed={selected}
                      className="w-full shrink-0"
                      key={period}
                      onClick={(): void => handleAmPmClick(period)}
                      ref={selected ? selectedPeriodButtonRef : undefined}
                      size="icon"
                      type="button"
                      variant={selected ? 'default' : 'ghost'}
                    >
                      {t(periodTranslationKey(period))}
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
